/* Drives snippets/sticky-add-to-cart.liquid.

   The bar deliberately owns no cart logic. It forwards its click to the real
   [data-add-to-cart] button in [data-product-form] and mirrors that button's
   label, disabled and loading state, plus the rendered price, so quantity,
   custom options, market pricing, promo-product-price.js and the quick-cart
   drawer keep working without being reimplemented here. */
(() => {
  if (window.stickyAddToCartInitialized) return;
  window.stickyAddToCartInitialized = true;

  const THUMBNAIL_WIDTH = 160;

  const sizedImageUrl = (src, width) => {
    if (!src) return '';
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (error) {
      return src;
    }
  };

  const setup = (bar) => {
    if (bar.dataset.stickyAtcReady === 'true') return;

    const section = bar.closest('.shopify-section') || document;
    const productContainer = section.querySelector('[data-section-type="product"]');
    const form = productContainer && productContainer.querySelector('[data-product-form]');
    const mainButton = form && form.querySelector('[data-add-to-cart]');

    // No buyable form means nothing to mirror — leave the bar hidden.
    if (!productContainer || !form || !mainButton) return;
    bar.dataset.stickyAtcReady = 'true';

    const button = bar.querySelector('[data-sticky-atc-button]');
    const buttonText = bar.querySelector('[data-sticky-atc-button-text]');
    const priceEl = bar.querySelector('[data-sticky-atc-price]');
    const compareEl = bar.querySelector('[data-sticky-atc-compare]');
    const variantEl = bar.querySelector('[data-sticky-atc-variant]');
    const imageEl = bar.querySelector('[data-sticky-atc-image]');

    const syncButton = () => {
      const mainText = mainButton.querySelector('[data-add-to-cart-text]');
      if (mainText && buttonText) {
        buttonText.textContent = mainText.textContent.trim();
      }

      const disabled =
        mainButton.hasAttribute('disabled') ||
        mainButton.getAttribute('aria-disabled') === 'true';
      button.disabled = disabled;
      if (disabled) {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
      }

      button.classList.toggle('loading', mainButton.classList.contains('loading'));
    };

    // Read the price back out of the DOM rather than formatting money here, so
    // whatever last wrote to the main price block wins in both places.
    const syncPrice = () => {
      const source =
        productContainer.querySelector('[data-promo-product-price]') ||
        productContainer.querySelector('.product__price');
      if (!source) return;

      const sourcePrice = source.querySelector('[data-price]');
      if (sourcePrice && priceEl) {
        priceEl.textContent = sourcePrice.textContent.trim();
      }

      if (compareEl) {
        const sourceCompare = source.querySelector('[data-compare-price]');
        const compareText = sourceCompare ? sourceCompare.textContent.trim() : '';
        compareEl.textContent = compareText;
        compareEl.hidden = compareText === '';
      }
    };

    const syncVariant = (variant) => {
      if (!variant) return;

      if (variantEl && variant.title) {
        variantEl.textContent = variant.title;
      }

      const media = variant.featured_media;
      const src = media && (media.preview_image ? media.preview_image.src : media.src);
      if (imageEl && src) {
        imageEl.src = sizedImageUrl(src, THUMBNAIL_WIDTH);
        if (media.alt) imageEl.alt = media.alt;
      }
    };

    // Two independent conditions: the real button has started to scroll away,
    // and the page has not been scrolled all the way to the end.
    //
    // This is measured directly rather than with an IntersectionObserver.
    // Threshold crossings can be skipped entirely when the viewport jumps —
    // a restored scroll position on reload, or the rating link anchoring to
    // the reviews — which would leave the bar stuck in a stale state.
    const stickyHeader = document.querySelector(
      '[data-section-type="header"][data-is-sticky="true"]'
    );
    const root = document.documentElement;
    const BOTTOM_SLACK = 2;

    // Chat bubbles and reward tabs live in the same bottom corner as the bar
    // and cover the button on a phone. They pin themselves with an inline
    // `bottom: …px !important`, which a stylesheet cannot beat, so the offset
    // has to be written as an inline important declaration too.
    const FLOATING_WIDGETS = [
      '#phntm-widget-host',
      '.yotpo-floater-widget-layout',
      '#gorgias-chat-container',
    ];
    let liftedWidgets = [];

    const liftWidgets = (height) => {
      liftedWidgets = [];
      FLOATING_WIDGETS.forEach((selector) => {
        document.querySelectorAll(selector).forEach((el) => {
          const computed = window.getComputedStyle(el);
          if (computed.position !== 'fixed') return;

          const base = Number.parseInt(computed.bottom, 10);
          // Anchored from the top, or not anchored at all — leave it alone.
          if (!Number.isFinite(base)) return;

          if (el.dataset.stickyAtcBase === undefined) {
            el.dataset.stickyAtcBase = String(base);
          }
          const original = Number(el.dataset.stickyAtcBase);
          el.style.setProperty('bottom', original + height + 'px', 'important');
          liftedWidgets.push(el);
        });
      });
    };

    const dropWidgets = () => {
      liftedWidgets.forEach((el) => {
        const original = Number(el.dataset.stickyAtcBase);
        if (Number.isFinite(original)) {
          el.style.setProperty('bottom', original + 'px', 'important');
        }
      });
      liftedWidgets = [];
    };

    // Chat widgets mount seconds after the page does, so a bar that appeared
    // first would not have found them.
    let lateLiftTimers = [];
    const clearLateLifts = () => {
      lateLiftTimers.forEach(window.clearTimeout);
      lateLiftTimers = [];
    };

    let framePending = false;
    let wasVisible = null;
    const measure = () => {
      framePending = false;

      const headerOffset = stickyHeader
        ? stickyHeader.getBoundingClientRect().height
        : 0;

      // The bar appears as soon as the real button's top slides under the
      // sticky header, rather than waiting for it to clear the viewport
      // entirely — waiting reads as a lag on a tall product page. A top still
      // below the header means we have not reached the button yet, so the bar
      // stays down rather than pre-empting it.
      const scrolledPastButton =
        mainButton.getBoundingClientRect().top < headerOffset;

      // Stand down only once the scroll actually reaches the end of the page.
      const atPageBottom =
        window.innerHeight + window.scrollY >= root.scrollHeight - BOTTOM_SLACK;

      const visible = scrolledPastButton && !atPageBottom;
      if (visible === wasVisible) return;
      wasVisible = visible;

      bar.classList.toggle('is-visible', visible);
      root.classList.toggle('sticky-atc-visible', visible);

      clearLateLifts();
      if (!visible) {
        dropWidgets();
        return;
      }

      const height = Math.round(bar.getBoundingClientRect().height);
      root.style.setProperty('--sticky-atc-height', height + 'px');
      liftWidgets(height);
      lateLiftTimers = [1000, 4000].map((delay) =>
        window.setTimeout(() => liftWidgets(height), delay)
      );
    };

    const scheduleMeasure = () => {
      if (framePending) return;
      framePending = true;
      window.requestAnimationFrame(measure);
    };

    // A resize can change the bar's height (the pickers move onto their own row
    // on a phone), so force the published height to be recomputed.
    const remeasure = () => {
      wasVisible = null;
      scheduleMeasure();
    };

    window.addEventListener('scroll', scheduleMeasure, { passive: true });
    window.addEventListener('resize', remeasure);
    // Late-loading media on this page shifts the button, so re-measure once
    // everything has settled.
    window.addEventListener('load', remeasure);
    measure();

    const buttonObserver = new MutationObserver(syncButton);
    buttonObserver.observe(mainButton, {
      attributes: true,
      attributeFilter: ['class', 'disabled', 'aria-disabled'],
      childList: true,
      subtree: true,
      characterData: true,
    });

    button.addEventListener('click', () => {
      if (button.disabled) return;
      mainButton.click();
    });

    // theme.js and promo-product-price.js both handle this event; read after
    // the frame so the bar reflects whichever of them wrote last.
    const onVariantChange = (event) => {
      syncVariant(event.detail && event.detail.variant);
      window.requestAnimationFrame(() => {
        syncPrice();
        syncButton();
      });
    };
    document.addEventListener('product:variant-change', onVariantChange);

    syncButton();
    window.requestAnimationFrame(syncPrice);

    bar.stickyAtcUnload = () => {
      buttonObserver.disconnect();
      window.removeEventListener('scroll', scheduleMeasure);
      window.removeEventListener('resize', remeasure);
      window.removeEventListener('load', remeasure);
      document.removeEventListener('product:variant-change', onVariantChange);
      clearLateLifts();
      dropWidgets();
      root.classList.remove('sticky-atc-visible');
      delete bar.dataset.stickyAtcReady;
    };
  };

  const init = (root) => {
    (root || document).querySelectorAll('[data-sticky-atc]').forEach(setup);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Theme editor: the section is replaced wholesale on every setting change.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-sticky-atc]').forEach((bar) => {
      if (typeof bar.stickyAtcUnload === 'function') bar.stickyAtcUnload();
    });
  });
})();
